import { Trash2 } from "lucide-react";
import React, { useMemo } from "react";
import { FieldErrors, UseFormRegister, UseFormSetError } from "react-hook-form";
import { URL_REGEX } from "../../../constants";
import { ToastVariant, useToastContext } from "../../../context";
import { CampaignOffer, CampaignTypes } from "../../../models/Campaign";
import { uploadFile } from "../../../utils/uploadFiles";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";

type Props = {
  campaign: CampaignTypes;
  setCampaign: React.Dispatch<React.SetStateAction<CampaignTypes>>;
  register: UseFormRegister<CampaignTypes>;
  errors?: FieldErrors<CampaignTypes>;
  setError?: UseFormSetError<any>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  isValid: boolean;
};

const StepTwo = ({
  campaign,
  setCampaign,
  register,
  errors,
  setError,
  isValid,
  setStep,
}: Props) => {
  const [fileDateTime, setFileDateTime] = React.useState<string>(
    new Date().toISOString()
  );
  const [loading, setLoading] = React.useState<boolean>(false);
  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const uploadFileHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosenFile = e.target.files?.[0];
    if (chosenFile) {
      const fileUrl = URL.createObjectURL(chosenFile);
      try {
        return await uploadFile(fileUrl, "campaigns");
      } catch (error) {
        console.error(error);
        addToast(ToastVariant.ERROR, {
          title: "Failed to upload file",
          description: "Try again later.",
        });
      }
    }
  };

  const handleOfferChange = (
    index: number,
    field: keyof CampaignOffer,
    value: string
  ) => {
    const updatedOffers = [...campaign.offers];

    updatedOffers[index][field] = value;

    setCampaign({ ...campaign, offers: updatedOffers });
  };

  const handleRemoveOffer = (index: number) => {
    const updatedOffers = campaign.offers.filter((_, i) => i !== index);

    setCampaign({ ...campaign, offers: updatedOffers });

    if (setError) {
      setError(`offers.${index}`, { type: "manual", message: "" });
    }
  };

  const continueButtonDisabled = useMemo(
    () =>
      campaign?.offers?.some(
        (offer) =>
          !offer.title || !offer.cta || !offer.description || !offer.url
      ),
    [campaign]
  );

  return (
    <div className="pb-4 flex flex-col gap-5 rounded-b-md">
      {campaign.offers.map((offer, index) => (
        <div
          key={index}
          className=" p-5 border-gray-300 rounded-md bg-gray-100"
        >
          <div className="flex">
            <div className="w-1/2 me-2">
              <label
                htmlFor={`offerTitle${index}`}
                className="block text-sm font-bold text-gray-700"
              >
                {t("stepTwo.offerTitle", { index: index + 1 })}{" "}
                <span className="text-orange-600">*</span>
              </label>

              <input
                type="text"
                id={`offerTitle${index}`}
                className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full"
                {...register(`offers.${index}.title`, {
                  required: { value: true, message: t("stepTwo.offerRequired") },
                  onChange: (e) =>
                    handleOfferChange(index, "title", e.target.value),
                })}
              />
              {errors?.offers && errors.offers[index]?.title && (
                <span className="text-red-500 text-sm">
                  {errors.offers[index]?.title?.message}
                </span>
              )}
            </div>

            <div className="w-1/2">
              <label
                htmlFor={`cta${index}`}
                className="block text-sm font-bold text-gray-700"
              >
                {t("stepTwo.callToAction", { index: index + 1 })}{" "}
                <span className="text-orange-600">*</span>
              </label>
              <input
                type="text"
                id={`cta${index}`}
                className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full"
                {...register(`offers.${index}.cta`, {
                  required: { value: true, message: t("stepTwo.ctaRequired") },
                  onChange: (e) =>
                    handleOfferChange(index, "cta", e.target.value),
                })}
              />
              {errors?.offers && errors.offers[index]?.cta && (
                <span className="text-red-500 text-sm">
                  {errors.offers[index]?.cta?.message}
                </span>
              )}
            </div>
          </div>

          <div className="flex mt-5">
            <div className="w-1/2 me-2">
              <label
                htmlFor={`campaignImage${index}`}
                className="block text-sm font-bold text-gray-700 mb-1"
              >
                {t("stepTwo.offerImage", { index: index + 1 })}
              </label>
              <div>
                {offer.image ? (
                  <div className="relative w-3/5 border border-gray-300 overflow-hidden rounded-md group">
                    <img
                      src={offer.image}
                      alt="offer"
                      className="w-full h-full object-contain"
                    />
                    <button
                      type="button"
                      className={twMerge(
                        "absolute opacity-0 transition-opacity ease-in top-2 left-2 text-white border bg-red-500 rounded-full p-2 font-light w-8 h-8 flex items-center focus:outline-none group-hover:opacity-100"
                      )}
                      onClick={() => handleOfferChange(index, "image", "")}
                    >
                      <Trash2 />
                    </button>
                  </div>
                ) : (
                  <input
                    type="file"
                    key={fileDateTime}
                    id={`campaignImage${index}`}
                    className="mt-1 p-1.5 border border-gray-300 rounded-md w-full bg-white"
                    onChange={(e) => {
                      setFileDateTime(new Date().toISOString());
                      setLoading(true);
                      uploadFileHandler(e)
                        .then((res) => {
                          if (res) {
                            handleOfferChange(
                              index,
                              "image",
                              res.storageUrl || ""
                            );
                          }
                          setLoading(false);
                        })
                        .catch(() => {
                          setLoading(false);
                          addToast(ToastVariant.ERROR, {
                            title: "Failed to upload file",
                          });
                        });
                    }}
                  />
                )}
              </div>
            </div>

            <div className="w-1/2">
              <label
                htmlFor={`campaignUrl${index}`}
                className="block text-sm font-bold text-gray-700"
              >
                {t("stepTwo.offerUrl", { index: index + 1 })}{" "}
                <span className="text-orange-600"> *</span>
              </label>
              <input
                type="text"
                id={`campaignUrl${index}`}
                className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                {...register(`offers.${index}.url`, {
                  required: { value: true, message: t("stepTwo.urlRequired")},
                  pattern: { value: URL_REGEX, message: t("stepTwo.invalidUrl") },
                  onChange: (e) =>
                    handleOfferChange(index, "url", e.target.value),
                })}
              />
              {errors?.offers && errors.offers[index]?.url && (
                <span className="text-red-500 text-sm">
                  {errors.offers[index]?.url?.message}
                </span>
              )}
            </div>
          </div>

          <div className="mt-5">
            <label
              htmlFor={`campaignDescription${index}`}
              className="block text-sm font-bold text-gray-700 mb-2"
            >
              {t("stepTwo.description", { index: index + 1 })}{" "}
              <span className="text-orange-600"> *</span>
            </label>
            <textarea
              id={`campaignDescription${index}`}
              className="p-2 border border-gray-300 rounded-md w-full resize-none"
              style={{ height: "150px" }}
              {...register(`offers.${index}.description`, {
                required: { value: true, message: t("stepTwo.descriptionRequired") },
                onChange: (e) =>
                  handleOfferChange(index, "description", e.target.value),
              })}
            ></textarea>
            {errors?.offers && errors.offers[index]?.description && (
              <span className="text-red-500 text-sm">
                {errors.offers[index]?.description?.message}
              </span>
            )}
          </div>
          <div className="mt-5">
            <button
              type="button"
              className="text-red-500 border border-red-500 rounded-md px-2 py-1"
              onClick={() => handleRemoveOffer(index)}
            >
              <div className="flex items-center">
                <span className="pe-2">{t("common.remove")}</span> <Trash2 />
              </div>
            </button>
          </div>
        </div>
      ))}

      <div className="mt-5">
        <button
          type="button"
          disabled={campaign.offers.length >= 3}
          className={`py-2 w-1/5 text-indigo-600 bg-indigo-200 hover:bg-indigo-300 rounded-md ${
            campaign.offers.length >= 3 ? "cursor-not-allowed" : ""
          }`}
          onClick={() => {
            setCampaign({
              ...campaign,
              offers: [
                ...campaign.offers,
                { title: "", cta: "", image: "", url: "", description: "" },
              ],
            });
          }}
        >
          {t("stepTwo.addOffer")}
        </button>
      </div>

      {loading && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-200 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-5 rounded-md">
            <span className="text-gray-500">Uploading Image...</span>
          </div>
        </div>
      )}

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          className="me-2 w-64 border border-indigo-600 text-indigo-600 py-2 rounded-md"
          onClick={() => setStep(1)}
        >
          {t("common.back")}
        </button>
        <button
          type="button"
          disabled={continueButtonDisabled}
          className="w-64 py-2 text-white rounded-md bg-indigo-600 disabled:bg-indigo-400"
          onClick={() => setStep(3)}
        >
          {t("common.continue")}
        </button>
      </div>
    </div>
  );
};

export default StepTwo;
